import {
  NoveltyRelationTypeType,
  OrderDirection,
  ReleaseEntryOrderByInput,
} from '../../__generated__/graphql'

import { type PreviewEntryData } from '@/components/PreviewEntry.vue'

import type { ResultOf } from '@graphql-typed-document-node/core'
import { GET_RELEASE_ITEMS, GET_RELEASE_ITEM_COLLECTIONS } from '@/queries'
import {
  CollectionFilterProps,
  formatVolumeRange,
  getVersionFromVolume,
  NoveltiesFilterProps,
  ReleaseEntryFilterProps,
  ReleaseEntrySorts,
  ReleaseItemsFilterProps,
} from '@/utils/index.ts'

type CollectionContentItem = {
  number?: string | null
  entryId?: string | null
  localizedSeriesTitle?: string | null
}

export type CollectionVolumeRangeSegments = {
  ownRange: string | null
  foreign: { title: string; range: string }[]
}

export function collectionItemsSpanMultipleEntries(
  releaseItems: { entryId?: string | null }[],
): boolean {
  const entryIds = new Set(
    releaseItems
      .map((item) => item.entryId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0),
  )
  return entryIds.size > 1
}

export function groupCollectionVolumeRangesByEntry(
  releaseItems: CollectionContentItem[],
  currentEntryId: string,
): CollectionVolumeRangeSegments {
  const ownNumbers: string[] = []
  const foreignByEntry = new Map<string, { title: string; numbers: string[] }>()
  for (const item of releaseItems) {
    if (typeof item.number !== 'string' || item.number.length === 0) continue
    if (!item.entryId || item.entryId === currentEntryId) {
      ownNumbers.push(item.number)
      continue
    }
    const group = foreignByEntry.get(item.entryId) ?? {
      title: item.localizedSeriesTitle || '',
      numbers: [],
    }
    group.numbers.push(item.number)
    foreignByEntry.set(item.entryId, group)
  }
  return {
    ownRange: ownNumbers.length > 0 ? formatVolumeRange(ownNumbers) : null,
    foreign: [...foreignByEntry.values()].map((group) => ({
      title: group.title,
      range: formatVolumeRange(group.numbers),
    })),
  }
}

export const getPaginationItemsForVolumes = (
  data: ResultOf<typeof GET_RELEASE_ITEMS>,
): PreviewEntryData[] =>
  data?.releaseItems?.map((d) => ({
    localizedSeriesTitle: d.localizedSeriesTitle,
    id: d.id,
    entryId: d.entryId,
    price: d.price,
    version: getVersionFromVolume(d),
    number: d.number,
    releaseDateAccuracy: d.releaseDateAccuracy,
    releaseDate: d.releaseDate,
    publisherName: d.publisherName,
    cover: d.cover,
    thumbHash: d.thumbHash,
    originalCover: d.originalCover,
    relationType: NoveltyRelationTypeType.Volume,
    relationId: d.id,
  })) ?? []

export const getPaginationItemsForReleaseItemCollections = (
  data: ResultOf<typeof GET_RELEASE_ITEM_COLLECTIONS>,
): PreviewEntryData[] => {
  const items =
    data?.releaseItemCollections?.map((d) => ({
      localizedSeriesTitle: d.localizedSeriesTitle,
      id: d.id,
      entryId: d.entryId,
      price: d.price,
      number: Number(d.number),
      name: d.name || d.volumeSuffix,
      releaseDate: d.releaseDate,
      releaseDateAccuracy: d.releaseDateAccuracy,
      publisherName: d.publisherName,
      cover: d.cover,
      thumbHash: d.thumbHash,
      relationType: NoveltyRelationTypeType.Collection,
      relationId: d.id,
      collectionVolumes: d.releaseItems?.map((ri) => ri.number) || [],
      collectionSpansEntries: collectionItemsSpanMultipleEntries(d.releaseItems ?? []),
    })) ?? []
  const maxNumber = Math.max(...items.map((i) => i.number))
  if (maxNumber === 1) {
    return items.map((i) => ({ ...i, number: 0, originalNumber: i.number }))
  }
  return items
}

export const getSortForVolumes = (
  sorts: ReleaseEntrySorts,
): { orderBy: ReleaseEntryOrderByInput[] } => {
  return {
    orderBy: [
      ...sorts
        .filter((s) => s.order != null)
        .map((s) => ({
          [s.property]: s.order,
        })),
      {
        number: OrderDirection.Asc,
      },
      {
        id: OrderDirection.Asc,
      },
    ],
  }
}

export const getDefaultReleaseItemFilter = (): ReleaseItemsFilterProps => {
  const now = new Date()

  return {
    selectedMedium: [],
    excludedMedium: [],
    selectedYear: now.getFullYear(),
    selectedMonth: now.getMonth() + 1,
    selectedPriceSpan: [0, 500],
    selectedPublisher: [],
    excludedPublisher: [],
    selectedTags: [],
    excludedTags: [],
    selectedDemographies: [],
    excludedDemographies: [],
    selectedRelationType: [],
    selectedCuratedLists: [],
    excludedCuratedLists: [],
    onlyShowFromCollection: false,
    showFirstVolumes: false,
    startDate: null,
    endDate: null,
  }
}
export const getDefaultReleaseEntryFilter = (): ReleaseEntryFilterProps => {
  return {
    selectedMedium: [],
    excludedMedium: [],
    selectedPublisher: [],
    excludedPublisher: [],
    selectedTags: [],
    excludedTags: [],
    selectedDemographies: [],
    excludedDemographies: [],
    startDate: null,
    endDate: null,
    excludeNoStartDate: true,
    selectedCuratedLists: [],
    excludedCuratedLists: [],
  }
}
export const getDefaultNoveltyFilter = (
  overwrite: Partial<NoveltiesFilterProps> = {},
): NoveltiesFilterProps => {
  return {
    selectedMedium: [],
    excludedMedium: [],
    selectedPublisher: [],
    excludedPublisher: [],
    selectedTags: [],
    excludedTags: [],
    selectedDemographies: [],
    excludedDemographies: [],
    selectedRelationType: [],
    selectedCuratedLists: [],
    excludedCuratedLists: [],
    startDate: null,
    endDate: null,
    onlyShowFromCollection: false,
    showFirstVolumes: false,
    ...overwrite,
  }
}
export const getDefaultCollectionFilter = (
  overwrite: Partial<CollectionFilterProps> = {},
): CollectionFilterProps => {
  return {
    selectedMedium: [],
    excludedMedium: [],
    selectedPublisher: [],
    excludedPublisher: [],
    selectedTags: [],
    excludedTags: [],
    selectedDemographies: [],
    excludedDemographies: [],
    startDate: null,
    endDate: null,
    excludeNoStartDate: true,
    collectionStatus: null,
    readStatus: null,
    statusFilter: 'collection',
    collectionFilterMode: undefined,
    ...overwrite,
  }
}
export const getDefaultVolumeSorts = (): ReleaseEntrySorts => {
  return [
    {
      label: 'sortReleaseDateLabel',
      property: 'releaseDate',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortPublisherLabel',
      property: 'publisherName',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortTitleLabel',
      property: 'localizedSeriesTitle',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortPriceLabel',
      property: 'price',
      order: OrderDirection.Asc,
    },
  ]
}
export const getDefaultDbSorts = (): ReleaseEntrySorts => {
  return [
    {
      label: 'sortPopularityLabel',
      property: 'popularityScore',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortReleaseDateLabel',
      property: 'startDate',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortPublisherLabel',
      property: 'publisherName',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortTotalVolumesLabel',
      property: 'totalVolumes',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortTotalReleasedVolumesLabel',
      property: 'totalReleasedVolumes',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortCreationDateLabel',
      property: 'creationDate',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortTitleLabel',
      property: 'internalName',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortRecommendationLabel',
      property: 'recommendationScore',
      order: OrderDirection.Desc,
      hidden: true,
    },
  ]
}
export const getDefaultEntrySorts = (): ReleaseEntrySorts => {
  return [
    {
      label: 'sortReleaseDateLabel',
      property: 'startDate',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortPublisherLabel',
      property: 'publisherName',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortTitleLabel',
      property: 'internalName',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortTotalVolumesLabel',
      property: 'totalVolumes',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortTotalReleasedVolumesLabel',
      property: 'totalReleasedVolumes',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortCreationDateLabel',
      property: 'creationDate',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortPopularityLabel',
      property: 'popularityScore',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortRecommendationLabel',
      property: 'recommendationScore',
      order: OrderDirection.Desc,
      hidden: true,
    },
  ]
}
export const getDefaultCollectionSorts = (): ReleaseEntrySorts => {
  return [
    {
      label: 'sortCollectionChangedLabel',
      property: 'lastCollectionChanged',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortReleaseDateLabel',
      property: 'startDate',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortTotalVolumesLabel',
      property: 'totalVolumes',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortTotalReleasedVolumesLabel',
      property: 'totalReleasedVolumes',
      order: OrderDirection.Desc,
    },
    {
      label: 'sortPublisherLabel',
      property: 'publisherName',
      order: OrderDirection.Asc,
    },
    {
      label: 'sortTitleLabel',
      property: 'internalName',
      order: OrderDirection.Asc,
    },
  ]
}
